/**
 * Pure selectors over ReconConfiguratorState.
 *
 * Live outside state.ts so the reducer module stays focused on
 * state transitions. Each selector is a pure function — no caching,
 * no hooks — so callers can wrap in useMemo where the perf matters.
 *
 * The cart-wide subdomain count selector is the headline tenant of
 * this file: the pricing engine uses a SINGLE pool of 5 free
 * subdomains across the entire cart (not 5-per-domain), so every
 * subdomain grid on the page needs to know the global selection
 * count to render its meter and badges correctly. Without this,
 * adding a second domain would show "0 of 5 free" in its own grid
 * even though the primary card had already used the whole budget.
 */

import type { ReconConfiguratorState } from "./state";
import { canQuote, canSubmit } from "./state";

/** Total selected subdomains across the entire cart. */
export function totalSelectedSubdomains(
  state: ReconConfiguratorState,
): number {
  const primaryApex = state.primaryDomain.trim().toLowerCase();
  // De-dup against the apex for safety even though the reducer
  // already filters it.
  const fromPrimary = state.primarySelectedSubs
    .map((h) => h.trim().toLowerCase())
    .filter((h) => h && h !== primaryApex).length;

  let fromAdditional = 0;
  for (const asset of state.additionalAssets) {
    if (asset.kind !== "domain") continue;
    const assetApex = asset.value.trim().toLowerCase();
    fromAdditional += asset.selectedSubs
      .map((h) => h.trim().toLowerCase())
      .filter((h) => h && h !== assetApex).length;
  }
  return fromPrimary + fromAdditional;
}

/** Returns the buyer-facing list of things still missing before
 *  the configurator can be submitted. Empty array means the form is
 *  fully ready. The order summary renders this verbatim so the
 *  buyer never has to wonder "why is the Pay button greyed out?"
 *
 *  Ordered by section position on the page so the hint reads
 *  top-to-bottom: scope → contact → attestation. */
export function getSubmitBlockers(state: ReconConfiguratorState): string[] {
  if (canSubmit(state)) return [];

  const blockers: string[] = [];

  if (!canQuote(state)) {
    blockers.push("Add a primary domain.");
  } else if (state.quote.kind !== "ok") {
    if (state.quote.kind === "loading") {
      blockers.push("Updating price…");
    } else if (state.quote.kind === "error") {
      blockers.push("We couldn't price your cart — try a different value.");
    } else {
      blockers.push("Waiting on the latest price.");
    }
  }

  // Contact info comes from the authenticated client profile, not
  // from a form on this page, so the contact blockers below only fire
  // in the edge case where useClientAuth().me hasn't synced into
  // state yet (auth bootstrapping race). In practice the page guards
  // on `isBootstrapping` before rendering so these stay defensive.
  if (!state.contact.name.trim()) {
    blockers.push("Account profile not loaded — refresh the page.");
  }
  if (!state.contact.email.trim()) {
    blockers.push("Account email not loaded — refresh the page.");
  }

  if (state.attestation.kind !== "ok") {
    if (state.attestation.kind === "error") {
      blockers.push("Reload the authorization text.");
    } else if (state.attestation.kind === "loading") {
      blockers.push("Loading the authorization text…");
    } else {
      blockers.push("Authorization text not loaded yet.");
    }
  } else if (!state.attestationChecked) {
    blockers.push("Check the authorization box.");
  } else if (!state.attestedName.trim()) {
    blockers.push("Type your legal name to authorize.");
  }

  // Defensive default if for some reason no specific blocker
  // matched — the form is incomplete but we don't know why. Better
  // than a silent-greyed-out button.
  if (blockers.length === 0) {
    blockers.push("Complete the steps above.");
  }
  return blockers;
}
